import { useState } from 'react';
import { InPageTabs, type InPageTab } from '../../components/InPageTabs';
import { GrowthValidationTab } from './GrowthValidationTab';
import { GrowthConnectionsTab } from './GrowthConnectionsTab';
import { GrowthDiscoveryTab } from './GrowthDiscoveryTab';
import { GrowthSignupLeadsTab } from './GrowthSignupLeadsTab';
import { useZoneHomeOnlyReport } from '../../components/ZoneContext';
import './growth.css';

/**
 * /admin/growth — the Growth team surface for the `nexa_growth` business role (ADR-075),
 * and the home of S6 · Growth (ADR-114).
 *
 * Was an ADR-036 `PlaceholderPage`. The stub's planned-features list is preserved as the
 * disabled tabs below rather than deleted — those are still the roadmap, and dropping them
 * would lose the only record of it on the surface itself.
 *
 * ⭐ THE TAB STRIP IS BUILT NOW, WITH ONE REAL TAB, DELIBERATELY. ADR-114 §5 and the
 * kickoff brief both call for it up front rather than as a later retrofit: the choice is
 * between adding the pattern now while there is one tab, or restructuring a single-purpose
 * page later while people are using it. The disabled future tabs also stop the surface
 * reading as "Growth = Validation", which it is not.
 *
 * The pattern is `InPageTabs` (ADR-074 §3.5), which declares itself the canonical in-page
 * tab strip. NOTE: `portal-staff-component-library.md` §4b still documents the older
 * `.portal-partners-tabs` shape — that entry predates InPageTabs and is stale; the
 * component's own header records that the legacy `AdminTabStrip` was removed.
 *
 * ⛔ ACCESS, STATED PLAINLY: `growth:read` / `growth:manage` are held by `nexa_admin` and
 * `super_admin`. The `nexa_growth` role itself STILL holds no permits — ADR-075 stubbed it
 * default-deny "until their catalogues exist", and writing that catalogue is a permission-
 * model decision rather than a side effect of building the first tool on the surface. So
 * the Growth team's own role cannot yet reach this screen; `nexa_admin` reaches it
 * transitionally, exactly as ADR-075 intended.
 */
type GrowthTabKey = 'validation' | 'discovery' | 'connections' | 'signup-leads' | 'funnels' | 'attribution' | 'cohorts';

const TABS: InPageTab<GrowthTabKey>[] = [
  { key: 'validation', label: 'Validation' },
  // ⭐ Discovery sits BEFORE Connections and AFTER Validation on purpose. It is upstream
  // of Validation in the pipeline — G0 builds the index G1 harvests against — but
  // Validation is the tab an operator opens daily and Discovery is the one that runs
  // itself. Ordering by "what you look at" beats ordering by data flow on a tab strip.
  { key: 'discovery', label: 'Discovery' },
  // ⭐ Connections is where Validation's four data sources get their API keys (D-009).
  // It sits beside Validation rather than inside it because all four are platform-wide
  // configuration, not properties of any one cohort — and because an operator setting the
  // platform up is doing a different job from one running a harvest.
  { key: 'connections', label: 'Connections' },
  // ADR-073 Am.5 — the abandonment report ADR-073 §2.7 asked for, as content of THIS surface
  // (A2.9: "the content of the existing Growth placeholder, not a parallel new surface"): the
  // public sign-ups that stopped before paying. Leads fan out across zones like the Interest queue.
  { key: 'signup-leads', label: 'Sign-up leads' },
  // Roadmap only — the former PlaceholderPage feature list, kept visible but inert.
  { key: 'funnels', label: 'Funnels', disabled: true },
  { key: 'attribution', label: 'Attribution', disabled: true },
  { key: 'cohorts', label: 'Cohort reporting', disabled: true },
];

export function GrowthPage() {
  const [active, setActive] = useState<GrowthTabKey>('validation');
  // ADR-123 Decision 1 — Growth still reads the home zone only (its zone scoping is a pending
  // product decision, ADR-122 "Explicitly deferred"), so the navigation bar says exactly that.
  useZoneHomeOnlyReport();

  return (
    <div className="portal-page growth-page">
      <header className="growth-page-header">
        <h2>Growth</h2>
        <p className="growth-page-intro">
          Target acquisition and growth analytics. <strong>Validation</strong> sources
          cold-outreach candidates and scores how confident we are that each business — and
          each contact — is real, before anything is ever sent. <strong>Discovery</strong>
          runs continuously behind it, building our own index of businesses resolved to a
          verified county.
        </p>
      </header>

      {/* A planned tab is inert rather than hidden: it shows the roadmap without
          pretending the screen exists. InPageTabs enforces that natively. */}
      <InPageTabs
        tabs={TABS}
        active={active}
        onChange={setActive}
        ariaLabel="Growth sections"
      />

      {active === 'validation' && <GrowthValidationTab />}
      {active === 'discovery' && <GrowthDiscoveryTab />}
      {active === 'connections' && <GrowthConnectionsTab />}
      {active === 'signup-leads' && <GrowthSignupLeadsTab />}
    </div>
  );
}
